<template>
<div>
    <div class="columns">
        <div class="column is-12">
            <div @click="toggle" class="provider-box">
                <div class="box-names">
                    <span class="account-name has-text-weight-medium">
                        <a>
                            <span v-if="!account.has_connected_provider" style="color: #FF8C7C;" class="tooltip" :data-tooltip="providerNotConnectedTooltip(account)"><font-awesome-icon icon="exclamation-triangle"/>&nbsp;</span>
                            {{ account.name }}
                        </a>
                    </span>
                    <span class="provider-name">{{ account.provider.name }}</span>
                </div>
                <div class="provider-stats">
                    <div class="statistic">
                        <span class="statistic-count tooltip is-tooltip-multiline" :data-tooltip="activeTotalDropletsTooltip(account)">{{ activeTotalDropletsLabel(account) }}</span>
                        <span class="statistic-label">{{ firstColumnLabel }}</span>
                    </div>
                    <div class="statistic">
                        <span class="statistic-count tooltip is-tooltip-multiline" :data-tooltip="activeTotalVolumesTooltip(account)">{{ activeTotalVolumesLabel(account) }}</span>
                        <span class="statistic-label">{{ secondColumnLabel }}</span>
                    </div>
                    <div class="statistic">
                        <span class="statistic-count tooltip is-tooltip-multiline" :data-tooltip="activeSnapshotsCountTooltip(account)">{{ account.active_snapshots_count }}</span>
                        <span class="statistic-label">Active Snapshots</span>
                    </div>
                    <div class="statistic">
                        <span class="statistic-count tooltip is-tooltip-multiline" :data-tooltip="lastSynchronizationTooltip">{{ lastSynchronization }}</span>
                        <span class="statistic-label">Last synchronization</span>
                    </div>
                </div>
                <div class="provider-toggle dropdown is-right is-hoverable dropdown-trigger">
                    <div v-if="!loading">
                        <span><font-awesome-icon icon="ellipsis-h"/></span>
                        <div class="dropdown-menu" role="menu">
                            <div class="dropdown-content">
                                <a @click.stop="showAccountBackupSettings" class="dropdown-item">
                                    <span><font-awesome-icon icon="tools" class="dropdown-action-icon"/>&nbsp;&nbsp;Default backup settings</span>
                                </a>
                                <a @click.stop="showDeletedCloudlets()" v-if="!includeDeletedCloudlets && expanded" class="dropdown-item">
                                    <span v-if="loadingBasedOnStatus">
                                        <font-awesome-icon class="fa-spin dropdown-action-icon" icon="spinner"/>&nbsp;
                                    </span>
                                    <span v-if="!loadingBasedOnStatus">
                                        <font-awesome-icon :icon="['far', 'eye']" class="dropdown-action-icon"/>
                                    </span>
                                    Show deleted cloudlets
                                </a>
                                <a @click.stop="hideDeletedCloudlets()" v-if="includeDeletedCloudlets && expanded" class="dropdown-item">
                                    <span v-if="loadingBasedOnStatus">
                                        <font-awesome-icon class="fa-spin dropdown-action-icon" icon="spinner"/>&nbsp;
                                    </span>
                                    <span v-if="!loadingBasedOnStatus">
                                        <font-awesome-icon :icon="['far', 'eye-slash']" class="dropdown-action-icon"/>
                                    </span>
                                    Hide deleted cloudlets
                                </a>
                                <a v-if="account.provider.slug == 'digital_ocean'" @click.stop="connectProvider()" class="dropdown-item">
                                    <font-awesome-icon :icon="['fas', 'link']" class="dropdown-action-icon"/>
                                    Reconnect account
                                </a>
                                <a @click.stop="deleteAccount()" class="dropdown-item"><font-awesome-icon icon="trash" class="dropdown-action-icon"/>Delete account</a>
                            </div>
                        </div>
                    </div>
                    <span v-show="loading"><font-awesome-icon class="fa-spin" icon="spinner"/></span>
                </div>
            </div>
        </div>
    </div>
    <transition name="slide">
        <div v-if="expanded" class="columns is-multiline" style="margin-bottom: 20px;">
            <div v-if="accountHasConnectedProvider" class="column" :class="{'is-4': accountHasCloudlets, 'is-6 is-offset-3': !accountHasCloudlets}">
                <scan-account :account="account"></scan-account>
            </div>
            <div v-if="!accountHasConnectedProvider" class="column is-6 is-offset-3">
                <connect-provider :account="account"></connect-provider>
            </div>
            <div v-for="cloudlet in cloudlets.data" class="column is-4">
                <cloudlet-widget :cloudlet="cloudlet"></cloudlet-widget>
            </div>
        </div>
    </transition>
</div>
</template>

<script>
import Api from '@/api';
import swal from 'sweetalert';
import ScanAccount from '@/components/Accounts/Scan.vue';
import CloudletWidget from '@/components/Cloudlets/Widget.vue';
import ConnectProvider from '@/components/Accounts/ConnectProvider.vue';

export default {
    components: {
        ScanAccount,
        CloudletWidget,
        ConnectProvider,
    },

    props: ['account'],

    data() {
        return {
            expanded: false,
            loading: false,
            includeDeletedCloudlets: false,
            loadingBasedOnStatus: false,
            cloudlets: [],
        }
    },

    mounted() {
        this.listenEvents();
    },

    methods: {
        toggle() {
            if (this.expanded) {
                // this.$router.push({'name': 'dashboard'});
                this.expanded = false;
                this.loading = false;
                return;
            }

            if (!this.expanded) {
                this.loading = true;
                this.getAccountCloudlets(() => {
                    this.expanded = true;
                    this.loading = false;
                });
            }
        },

        getAccountCloudlets(successCallback) {
            let endpoint = 'accounts/' + this.account.id + '/cloudlets';

            var params = {};

            if (this.includeDeletedCloudlets) {
                params.include_deleted = this.includeDeletedCloudlets;
            }

            Api().get(endpoint, { params })
                .then((response) => {
                    this.cloudlets = response.data;

                    let refreshedCloudletsCount = response.data.count;
                    Event.$emit('accountCloudletsRefreshed', this.account.id, refreshedCloudletsCount);

                    if (typeof successCallback === 'function') {
                        successCallback();
                    }
                })
                .catch((error) => {

                })
                .finally(() => {

                });
        },

        showAccountBackupSettings() {
            Event.$emit('showAccountBackupSettings', this.account.id);
        },

        listenEvents() {
            Event.$on('refreshAccountCloudlets', (accountId, callback) => {
                if (this.account.id == accountId) {
                    this.getAccountCloudlets(callback);
                }
            });
        },

        activeSnapshotsCountTooltip(account) {
            return 'You have ' + account.active_snapshots_count + ' active snapshots on the account "' + account.name + '" created by Weap.io';
        },

        activeTotalDropletsTooltip(account) {
            if (!account.total_droplets_count) {
                return 'The account "' + account.name + '" has no cloudlets that can be backed-up by Weap.io';
            }
            return 'Weap.io is taking care of ' + account.active_droplets_count + ' out of your ' + account.total_droplets_count + ' cloudlets from the account "' + account.name + '"';
        },

        activeTotalDropletsLabel(account) {
            if (!account.total_droplets_count) {
                return '-';
            }
            return account.active_droplets_count + '/' + account.total_droplets_count;
        },

        activeTotalVolumesTooltip(account) {
            if (!account.total_volumes_count) {
                return 'The account "' + account.name + '" has no volumes that can be backed-up by Weap.io';
            }
            return 'Weap.io is taking care of ' + account.active_volumes_count + ' out of your ' + account.total_volumes_count + ' volumes from the account "' + account.name + '"';
        },

        activeTotalVolumesLabel(account) {
            if (!account.total_volumes_count) {
                return '-';
            }
            return account.active_volumes_count + '/' + account.total_volumes_count;
        },

        providerNotConnectedTooltip(account) {
            return 'This account is not connected to a ' + account.provider.name + ' account';
        },

        showDeletedCloudlets() {
            this.includeDeletedCloudlets = true;
            this.loadingBasedOnStatus = true;

            let vm = this;

            Event.$emit('refreshAccountCloudlets', this.account.id, () => {
                vm.loadingBasedOnStatus = false;
            });
        },

        hideDeletedCloudlets() {
            this.includeDeletedCloudlets = false;
            this.loadingBasedOnStatus = true;

            let vm = this;

            Event.$emit('refreshAccountCloudlets', this.account.id, () => {
                vm.loadingBasedOnStatus = false;
            });
        },

        deleteAccount() {
            swal({
                title: 'Are you sure?',
                text: 'This action cannot be undone. If there are any active snapshots left, you will have to manually delete them from the ' + this.account.provider.name + ' dashboard. The delete process may take some time.',
                dangerMode: true,
                buttons: {
                    cancel: 'Cancel',
                    confirm: {
                        text: 'Yes, delete it!',
                        value: true,
                        closeModal: false,
                    }
                },
                icon: 'warning',
            })
            .then((value) => {
                if (!value) {
                    swal.close();
                    return;
                }

                let endpoint = 'accounts/' + this.account.id;

                return Api().delete(endpoint)
                    .then((response) => {
                        Event.$emit('refreshAccounts', () => {
                            swal('Success!', 'The account was deleted.', 'success');
                        });
                    })
                    .catch((error) => {
                        swal('Oops!', 'Something happened and we could not delete the account.', 'error');
                    })
                    .finally(() => {
                        //
                    });
            });
        },

        connectProvider() {
            // if (this.connecting) {
            //     return;
            // }

            localStorage.accountPendingAssociation = JSON.stringify({
               id: this.account.id,
            });

            this.connecting = true;
            window.location.href = this.account.authorization_url;
        },
    },

    computed: {
        toggleExpandIcon() {
            if (this.expanded) {
                return 'chevron-up';
            }

            return 'chevron-down';
        },

        lastSynchronization() {
            if (this.account.last_synchronization_human_diff == null) {
                return '-';
            }
            return this.account.last_synchronization_human_diff;
        },

        lastSynchronizationTooltip() {
            if (!this.account.last_synchronized_at) {
                return 'This account was never synchronized.';
            }
            return 'Last successful synchronization of this account occurred at ' + this.account.last_synchronized_at + ' UTC.';
        },

        accountHasCloudlets() {
            if (this.cloudlets.length > 0) {
                return true;
            }

            return this.account.has_cloudlets;
        },

        accountHasConnectedProvider() {
            if (this.accountHasCloudlets) {
                return true;
            }

            return  this.account.has_connected_provider;
        },

        firstColumnLabel() {
            if (this.account.provider.slug == 'digital_ocean') {
                return 'Active Droplets/Total Droplets';
            }

            if (this.account.provider.slug == 'aws') {
                return 'Active instances/Total instances';
            }

            if (this.account.provider.slug == 'hetzner') {
                return 'Active cloud servers/Total cloud servers';
            }

            if (this.account.provider.slug == 'ovh') {
                return 'Active instances/Total instances';
            }

            if (this.account.provider.slug == 'aws_lightsail') {
                return 'Active instances/Total instances';
            }

            if (this.account.provider.slug == 'upcloud') {
                return 'Active instances/Total instances';
            }

            if (this.account.provider.slug == 'exoscale') {
                return 'Active instances/Total instances';
            }

            if (this.account.provider.slug == 'gcp') {
                return 'Active instances/Total instances';
            }

            if (this.account.provider.slug == 'alibaba_cloud') {
                return 'Active instances/Total instances';
            }

            if (this.account.provider.slug == 'scaleway') {
                return 'Active instances/Total instances';
            }
        },

        secondColumnLabel() {
            if (this.account.provider.slug == 'digital_ocean') {
                return 'Active Volumes/total Volumes';
            }
            return '-';
        },
    }
}
</script>

<style>
.slide-enter-active {
    -moz-transition-duration: 0.3s;
     -webkit-transition-duration: 0.3s;
     -o-transition-duration: 0.3s;
     transition-duration: 0.3s;
     -moz-transition-timing-function: ease-in;
     -webkit-transition-timing-function: ease-in;
     -o-transition-timing-function: ease-in;
     transition-timing-function: ease-in;
}

.slide-leave-active {
   -moz-transition-duration: 0.3s;
   -webkit-transition-duration: 0.3s;
   -o-transition-duration: 0.3s;
   transition-duration: 0.3s;
   -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
   -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
   -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
   transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
}

.slide-enter-to, .slide-leave {
   max-height: 500px;
   overflow: hidden;
}

.slide-enter, .slide-leave-to {
   overflow: hidden;
   max-height: 0;
}
</style>
